import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Flex, Text, Input, Button, IconButton, getPaletteColor, Card, CloseButton } from 'pcln-design-system';
import Popover from 'pcln-popover';
import styled from 'styled-components';
import MicIcon from '../svgs/MicIcon';
import broadcast, { BroadcastType, usePennySubscriber } from '../utils/pennyBroadcast';
import StopCircleIcon from '../svgs/StopCircleIcon';
const UserInputContainer = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "UserInputBar__UserInputContainer",
  componentId: "sc-1ravw2d-0"
})(["", " padding:0 8px;align-items:center;background-color:", ";justify-content:space-between;"], props => {
  if (props.isRecording) {
    return `
        height: 67px;
        margin: 0px;
        border-top: 1px solid ${getPaletteColor('border.tint')(props)};
        border-radius: 0;
      `;
  } else {
    return `
        height: 67px;
        margin: 1px;
        border-radius: 0 0 24px 24px;
      `;
  }
}, props => getPaletteColor('background.lightest')(props));
const MicContainer = /*#__PURE__*/styled(Box).withConfig({
  displayName: "UserInputBar__MicContainer",
  componentId: "sc-1ravw2d-1"
})(["height:44px;width:44px;"]);
const StyledInput = /*#__PURE__*/styled(Input).withConfig({
  displayName: "UserInputBar__StyledInput",
  componentId: "sc-1ravw2d-2"
})(["border-radius:24px;border-style:none;"]);
const PopoverContent = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "UserInputBar__PopoverContent",
  componentId: "sc-1ravw2d-3"
})(["z-index:1000;"]);
function UserInputBar(props) {
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showVoiceTooltip, setShowVoiceTooltip] = useState(false);
  const inputRef = useRef(null);
  function pennySubscriptionHandler(event) {
    if (event.data.type === BroadcastType.USER_IS_SILENT) {
      props.onStopRecording();
    } else if (event.data.type === BroadcastType.VOICE_RECORDING_STARTED) {
      setIsRecording(true);
    } else if (event.data.type === BroadcastType.VOICE_RECORDING_STOPPED) {
      setIsRecording(false);
    } else if (event.data.type === BroadcastType.AUDIO_PLAYBACK_STARTED) {
      setIsAudioPlaying(true);
    } else if (event.data.type === BroadcastType.AUDIO_QUEUE_DRAINED) {
      setIsAudioPlaying(false);
    }
  }
  const pennyBroadcastListener = useCallback(pennySubscriptionHandler, []);
  usePennySubscriber(pennyBroadcastListener);
  useEffect(() => {
    if (typeof localStorage !== 'undefined' && localStorage.getItem) {
      const userClosedTooltip = localStorage.getItem('userClosedVoiceModeTooltip');
      if (userClosedTooltip !== 'true') {
        setShowVoiceTooltip(true);
      } else {
        setShowVoiceTooltip(false);
      }
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  function handleTextChange(e) {
    setTextInput(e.target.value);
  }
  function handleTextSubmit() {
    setTextInput('');
    if (props.onTextSubmit) {
      props.onTextSubmit(textInput);
    }
  }
  function closeTooltip() {
    setShowVoiceTooltip(false);
    if (typeof localStorage !== 'undefined' && localStorage.setItem) {
      localStorage.setItem('userClosedVoiceModeTooltip', 'true');
    }
  }
  function handleEnterKeyPress(e) {
    if (e.key === 'Enter') {
      handleTextSubmit();
    }
  }
  function handleRecordButtonClick() {
    closeTooltip();
    if (props.onRecord) {
      props.onRecord();
    }
  }
  function haltPlayback() {
    broadcast({
      type: BroadcastType.INTERRUPT_AUDIO_PLAYBACK,
      sender: 'UserInputBar'
    });
    if (props.onStopPlayback) {
      props.onStopPlayback();
    }
  }
  return React.createElement(UserInputContainer, {
    isRecording: isRecording
  }, React.createElement(Card, {
    mr: 2,
    width: '100%',
    borderRadius: 24,
    boxShadowSize: 'sm'
  }, React.createElement(StyledInput, {
    id: 'chatInput',
    "data-testid": 'chatInput',
    autoComplete: 'off',
    onChange: handleTextChange,
    onClick: closeTooltip,
    value: textInput,
    placeholder: 'Ask Penny a question',
    onKeyPress: handleEnterKeyPress,
    mr: 2,
    ref: inputRef
  })), textInput && React.createElement(Button, {
    onClick: handleTextSubmit,
    "data-testid": 'chatSubmitButton',
    mr: 2
  }, "Submit"), React.createElement(Popover
  // eslint-disable-next-line react/jsx-no-bind
  , {
    // eslint-disable-next-line react/jsx-no-bind
    renderContent: ({
      handleClose
    }) => React.createElement(PopoverContent, {
      id: 'voice-mode-tooltip',
      p: 2,
      style: {
        pointerEvents: 'auto'
      }
    }, React.createElement(Box, null, React.createElement(MicIcon, {
      width: '30'
    })), React.createElement(Box, {
      px: 2,
      "data-testid": 'voice-mode-tooltip'
    }, React.createElement(Text, {
      fontWeight: 'bold'
    }, "Introducing Voice Chat"), React.createElement(Text, null, "Hands-free convenience. Tap the mic to start.")), React.createElement(Box, null, React.createElement(CloseButton, {
      onClick: handleClose
    }))),
    idx: 'voiceModeTooltip',
    zIndex: 1000,
    placement: 'top',
    ariaLabel: 'Voice Mode Information',
    width: 290,
    stopPropagation: false,
    toggleIsOpenOnClick: false,
    openOnHover: false,
    openOnFocus: false,
    openOnMount: false,
    onClose: closeTooltip,
    isOpen: showVoiceTooltip && props.enableVoiceTooltip
  }, React.createElement(MicContainer, null, isRecording ? React.createElement(IconButton, {
    onClick: props.onStopRecording,
    "data-testid": 'stopRecordingButton',
    icon: React.createElement(StopCircleIcon, null)
  }) : isAudioPlaying ? React.createElement(IconButton, {
    onClick: haltPlayback,
    "data-testid": 'stopPlaybackButton',
    icon: React.createElement(StopCircleIcon, null)
  }) : React.createElement(IconButton, {
    onClick: handleRecordButtonClick,
    "data-testid": 'recordButton',
    icon: React.createElement(MicIcon, null)
  }))));
}
export default UserInputBar;